import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { createPortal } from 'react-dom';
import { ArrowRight, Download, ExternalLink, Info } from 'lucide-react';
import { attritionDashboardAPI, type DashRichFilterParams } from '../../services/api';
import { buildCsv, downloadCsv } from '../../utils/csv';
import { Modal, SearchBox } from './DashboardPrimitives';
import { InlineLoader } from '../loading';
import { useTableSort, SortTh } from './useTableSort';
import { useStoreRegions } from '../../hooks/useStoreRegions';
import { pmLaunchHref, usePmLaunchStatus } from '../pm-launch/usePmLaunchStatus';
import { useDashboardList } from './useDashboardList';
import { EXITS_SLUG } from './lists/attrition';

/**
 * Dashboard → Attrition tab. Ports the 1Office Project Management attrition
 * dashboard so the two report the same numbers for the same project.
 *
 * The chart is hand-drawn SVG rather than recharts, matching both the source
 * dashboard and DashboardPrimitives' existing hand-rolled StackedBarChart. A
 * charting library buys nothing here: the bars are a fixed 6-column stack whose
 * one fiddly requirement — rounded corners on the STACK, not on each segment —
 * is a three-line clipPath in raw SVG and a fight in a library.
 */

// ── Types (mirror dashboard-attrition.controller) ────────────────────────────

interface Bucket {
  key: string;
  label: string;
  hex: string;
  /** The stage of employment the band belongs to — one colour family per stage.
   *  Absent on Unclassified, and on a payload from an API older than the grouping. */
  group?: string;
  groupLabel?: string;
  /** Hover text. Exit types carry one; tenure bands do not need one. */
  hint?: string;
}
/** One group of the month's exits (server-grouped). `exits` counts the leavers; the three
 *  split counts are the exits HR processed in the month, so they need not add up to it. An API
 *  one deploy behind does not send them. */
interface AttrBucket { key: string; exits: number; managed?: number; unmanaged?: number; notRecorded?: number }
interface AttritionBreakdown {
  month: string;
  totalExits: number;
  /** Exits HR processed in the month: the total of the split counts. Absent from an older API. */
  processedExits?: number;
  byManager: AttrBucket[];
  byTrade: AttrBucket[];
  byCategory: AttrBucket[];
  byCity: AttrBucket[];
  byState: AttrBucket[];
  unmapped: { store: number };
}
/** Region is rolled up CLIENT-side from the states — there is no region column. */
type AttrDim = 'manager' | 'trade' | 'category' | 'region' | 'city';
const ATTR_DIMS: Array<{ key: AttrDim; label: string }> = [
  { key: 'manager', label: 'Manager' },
  { key: 'trade', label: 'Trade' },
  { key: 'category', label: 'Category' },
  { key: 'region', label: 'Region' },
  { key: 'city', label: 'City' },
];
const NO_MANAGER = '— No manager mapped —';
const NO_STORE_ATTR = '— No store mapped —';

/** What the Attrition Rate chart stacks by. */
type ChartView = 'tenure' | 'exitType';
const CHART_VIEWS: Array<{ key: ChartView; label: string }> = [
  { key: 'tenure', label: 'By tenure' },
  { key: 'exitType', label: 'Managed vs Unmanaged' },
];
const NOT_RECORDED = 'not-recorded';
/** Hover on the Managed / Unmanaged / Not recorded columns: what they count. */
const PROCESSED_HINT = 'Exits HR processed in 1Office this month, as Project Management counts them. Not the leavers, so the three need not add up to Exits.';

interface MonthlyRow {
  month: string;
  label: string;
  headcount: number;
  exits: number;
  classifiedExits: number;
  unclassifiedExits: number;
  rawActual: number;
  actual: number;
  projected: number;
  tenureBuckets: Array<{ key: string; label: string; exits: number; rateContribution: number }>;
  /** Exits HR processed in 1Office this month, by exit type (Project Management's Attrition
   *  Classification card) — not a split of `exits`. Absent on a payload from an older API. */
  exitTypes?: Array<{ key: string; label: string; exits: number; rateContribution: number }>;
}
/** A leaver's stored Employee Score (attrition-scores.service). */
interface LeaverScore {
  /** The newest stored total on or before the exit month. */
  total: number;
  /** Its month, 'YYYY-MM' — earlier than the exit month when that month has no stored score. */
  month: string;
  /** The average over the scored months of the six ending at `month`. */
  avg6m: number;
  /** How many of those six had a score. */
  monthsAveraged: number;
}
interface ExitedEmployee {
  employeeCode: string;
  employeeName: string | null;
  project: string | null;
  budget: string | null;
  joiningDate: string | null;
  /** null for someone in the Managed / Unmanaged count who has not left yet. */
  exitDate: string | null;
  /** The leaving month; null for someone in the Managed / Unmanaged count only. */
  month: string | null;
  tenureDays: number | null;
  tenureBucketKey: string;
  tenureBucketLabel: string;
  /** 'managed', 'unmanaged' or 'not-recorded'. */
  exitType?: string;
  /** The day HR processed their exit in 1Office, as the Managed / Unmanaged count files it, and
   *  its month; null when HR has processed none. Absent from an older API. */
  processedOn?: string | null;
  processedMonth?: string | null;
  /** Where the leaver last worked — their last store on record. null when none is. */
  store?: string | null;
  city?: string | null;
  state?: string | null;
  /** Present only when meta.scores_shown; null when no month up to the exit is scored. */
  score?: LeaverScore | null;
}
interface AttritionData {
  anchorMonth: string;
  months: Array<{ ym: string; label: string; startDate: string; endDate: string }>;
  monthly: MonthlyRow[];
  employees: ExitedEmployee[];
  buckets: Bucket[];
  /** Legend and colours of the Managed vs Unmanaged view, in stacking order. */
  exitTypes?: Bucket[];
  /** Where the leavers in view were last posted — independent of the location filter. */
  locations?: AttritionLocation[];
  /** The trades of those stores — independent of the Store Trade filter. */
  trades?: string[];
}

/** One state + city a leaver was last posted in. */
export interface AttritionLocation { state: string; city: string }

/** What the tab learns from a response that the dashboard's Filters modal needs. */
export interface AttritionFacets {
  /** The tenure bands the API counts, Unclassified included — the Tenure options. */
  tenures: Array<{ key: string; label: string }>;
  locations: AttritionLocation[];
  /** The trades the leavers' last stores have — extra Store Trade options. */
  trades: string[];
}
interface AttritionMeta {
  roster_size: number;
  dropped_no_exit_date: number;
  exits_feed_suspect: boolean;
  unavailable_tiles: string[];
  /** A look-back-safe filter (Role / AM / TH / Budget / Region / State / City)
   *  narrowed the population. */
  filtered?: boolean;
  /** One of those was a location filter: people are placed by last store on record. */
  location_filtered?: boolean;
  /** Leavers in the window a location filter could not place — no store on record —
   *  and so missing from every figure. null when no location filter is on. */
  location_unmapped_exits?: number | null;
  /** The store lookup behind the Region / State / City columns failed: a blank there
   *  is a failure this time, not "no store on record". */
  location_lookup_failed?: boolean;
  /** The project has a scorecard: the leaver list shows Last score and 6-month avg. */
  scores_shown?: boolean;
  /** The score read failed, so those two columns are dashes this time. */
  scores_lookup_failed?: boolean;
  /** 1Office sends the Managed / Unmanaged choice. Off, the tab shows no split at all. */
  exit_types_shown?: boolean;
  /** 'processed': the split counts exits HR processed, by the month it processed them. */
  exit_types_basis?: string;
  /** The EXITS were narrowed to some tenure bands; the headcount was not. Every rate
   *  is then those bands' share of the group's attrition rate. */
  tenure_filtered?: boolean;
  tenure_labels?: string[];
  /**
   * Whose population these figures cover. 'team' means the viewer is not a
   * project-wide viewer and is seeing their own reporting line — a different
   * population from the project, so a legitimately different rate.
   */
  scope_mode?: 'project' | 'team';
  /**
   * People in that reporting line OTHER THAN the viewer, on this project, before
   * their own filter choices. 0 means they genuinely manage nobody here. Counts
   * leavers too, so a manager whose whole line exited still sees their attrition.
   */
  scoped_reportee_count?: number | null;
}

// ── Formatting ───────────────────────────────────────────────────────────────

const fmtDate = (iso: string | null): string => {
  if (!iso) return '—';
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  return y && m && d ? `${d}-${m}-${y}` : '—';
};

/** Matches PM's formatTenureDays: days → months → years, one decimal. */
const fmtTenure = (days: number | null): string => {
  if (days == null || !Number.isFinite(days)) return '—';
  if (days < 30) return `${days} day${days === 1 ? '' : 's'}`;
  if (days < 365) return `${(days / 30.4375).toFixed(1)} months`;
  return `${(days / 365.2425).toFixed(1)} years`;
};

/**
 * A month's rate for display. One decimal, as the API rounds it — except that a share
 * too small to survive that rounding is shown to two, so a bar that is visibly drawn
 * is never labelled "0%". That happens under a tenure filter: one exit in a selected
 * band over a headcount of 3,000 is 0.03%.
 */
const fmtRate = (m: { actual: number; rawActual: number; exits: number }): string =>
  (m.exits > 0 && m.actual === 0 ? m.rawActual.toFixed(2) : String(m.actual));

const monthTitle = (ym: string): string => {
  const [y, m] = ym.split('-').map(Number);
  if (!y || !m) return ym;
  return `${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][m - 1]} ${y}`;
};

/** 'YYYY-MM' moved by `delta` months. */
const shiftMonth = (ym: string, delta: number): string => {
  const [y, m] = ym.split('-').map(Number);
  if (!y || !m) return ym;
  const i = y * 12 + (m - 1) + delta;
  return `${Math.floor(i / 12)}-${String((i % 12) + 1).padStart(2, '0')}`;
};

/** Two decimals, as the Employee Score page shows a total and its 6-month average. */
const fmtScore = (n: number): string => n.toFixed(2);

/** Hover on Last score: which month it is, and why it is not the exit month. */
const lastScoreTitle = (r: ExitedEmployee, failed: boolean): string => {
  const s = r.score;
  if (!r.month) return 'Not left in these months, so there is no exit month to read a score for';
  if (!s) return failed ? 'Employee Scores could not be loaded just now' : `No final Employee Score for ${monthTitle(r.month)} or any month before it`;
  let text = `Employee Score for ${monthTitle(s.month)} as stored`;
  if (s.month < r.month) {
    const next = shiftMonth(s.month, 1);
    text += next === r.month
      ? `; ${monthTitle(r.month)} has no final score`
      : `; ${monthTitle(next)} to ${monthTitle(r.month)} have no final score`;
  }
  return text;
};

/** Hover on 6-month avg: the months it covers. */
const avgTitle = (s: LeaverScore): string =>
  `Average of the Employee Scores stored for ${monthTitle(shiftMonth(s.month, -5))} to ${monthTitle(s.month)} (${s.monthsAveraged} of 6 months scored)`;

// ── The chart ────────────────────────────────────────────────────────────────

const W = 720;
const H = 262;
const PAD = { top: 34, right: 18, bottom: 25, left: 18 };
const CHART_W = W - PAD.left - PAD.right;
const CHART_H = H - PAD.top - PAD.bottom;
const BAR_GAP = 28;
const BASE_Y = PAD.top + CHART_H;
/** White left between two stacked bands, in viewBox units (~2px on screen). The gap,
 *  not a stroke, is what keeps two neighbouring shades of one family apart. */
const SEG_GAP = 1.5;
/** Vertical room one value label needs, so two of them are never drawn on top of each other. */
const LABEL_H = 11;

/** Legend entries in stacking order, split into runs that share a stage. */
const legendGroups = (buckets: Bucket[]): Array<{ label: string | null; items: Bucket[] }> => {
  const groups: Array<{ label: string | null; items: Bucket[] }> = [];
  for (const b of buckets) {
    const label = b.groupLabel ?? null;
    const last = groups[groups.length - 1];
    if (last && last.label === label) last.items.push(b);
    else groups.push({ label, items: [b] });
  }
  return groups;
};

/** The By tenure view. The Managed vs Unmanaged view is ExitTypeChart. */
function AttritionChart({ monthly, buckets, onPick, tenureFiltered }: {
  monthly: MonthlyRow[];
  /** The tenure bands, the legend in stacking order. */
  buckets: Bucket[];
  onPick: (month: string, bucketKey?: string) => void;
  /** The exits were narrowed to some tenure bands, so `m.exits` is not the month's
   *  total and a band's share of it must not be called a share of "exits". */
  tenureFiltered?: boolean;
}) {
  const n = monthly.length;
  const barWidth = Math.min((CHART_W - BAR_GAP * (n - 1)) / n, 62);
  const totalBars = n * barWidth + (n - 1) * BAR_GAP;
  const offsetX = PAD.left + (CHART_W - totalBars) / 2;

  // Scale to the tallest thing drawn — including a projection, or the ghost bar
  // would overflow the plot area in a high-attrition month.
  const maxValue = Math.max(
    ...monthly.map((m) => (m.projected > 0 ? Math.max(m.rawActual, m.projected) : m.rawActual)),
    1,
  );

  const hexByKey = new Map(buckets.map((b) => [b.key, b.hex]));
  const hintByKey = new Map(buckets.map((b) => [b.key, b.hint]));
  const unclassifiedHex = hexByKey.get('unclassified') ?? '#9CA3AF';
  const anyProjected = monthly.some((m) => m.projected > 0);
  const anyUnclassified = monthly.some((m) => m.unclassifiedExits > 0);
  // Bands with no exit anywhere in the window stay in the legend: a reader checking
  // "did anyone leave in their first week?" needs the swatch to see that it is absent
  // from every bar, and a legend that reshuffles with the data cannot be learned.
  const groups = legendGroups(buckets.filter((b) => b.key !== 'unclassified' || anyUnclassified));

  return (
    <>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="w-full"
        role="img"
        aria-label="Monthly attrition rate stacked by employee tenure"
      >
        <line x1={PAD.left} x2={W - PAD.right} y1={BASE_Y} y2={BASE_Y} stroke="#E5E7EB" strokeWidth="0.75" />

        {monthly.map((m, i) => {
          const x = offsetX + i * (barWidth + BAR_GAP);
          const actualH = maxValue > 0 ? (m.rawActual / maxValue) * CHART_H : 0;
          const actualY = BASE_Y - actualH;
          const projH = m.projected > 0 ? (m.projected / maxValue) * CHART_H : 0;
          const projY = BASE_Y - projH;
          const clipId = `attrition-stack-${m.month}`;
          const showProjected = m.projected > 0 && m.projected !== m.actual;
          // Both value labels sit ABOVE their bar, in ink — never inside it. Inside
          // only worked while every band was dark enough for white text; several of
          // the bands are now light. The projected label is lifted clear of the actual
          // one when the two bars are close in height.
          const actualLabelY = actualY - 4;
          const projLabelY = Math.min(projY - 4, actualLabelY - LABEL_H);

          // Segments stack BOTTOM-UP, shortest tenure at the bottom, so a tall warm
          // base reads immediately as "we are losing new joiners".
          let segBottom = BASE_Y;
          const segments = [
            ...m.tenureBuckets.map((seg) => ({ key: seg.key, label: seg.label, exits: seg.exits, fill: hexByKey.get(seg.key) ?? '#94A3B8', points: seg.rateContribution as number | null })),
            { key: 'unclassified', label: 'Unclassified', exits: m.unclassifiedExits, fill: unclassifiedHex, points: null },
          ].filter((seg) => seg.exits > 0 && m.exits > 0);

          return (
            <g key={m.month}>
              <title>
                {`${m.label}: ${fmtRate(m)}% ${tenureFiltered ? 'of headcount' : 'actual'} from ${m.exits} ${tenureFiltered ? 'exits in the selected tenure bands' : 'exits'}${m.projected > 0 ? `; ${m.projected}% projected` : ''}`}
              </title>

              {/* Ghost projection bar, drawn BEHIND the actual stack. Neutral grey with
                  a dashed edge rather than a pale blue: blue is now a tenure family,
                  and a tinted ghost read as one more band. */}
              {m.projected > 0 && (
                <rect
                  x={x + 0.5}
                  y={projY}
                  width={barWidth - 1}
                  height={Math.max(projH, 2)}
                  rx={5}
                  fill="#F3F4F6"
                  stroke="#9CA3AF"
                  strokeWidth="0.75"
                  strokeDasharray="3 2"
                />
              )}

              {/* Rounding the STACK (not each segment) needs a clip. */}
              <clipPath id={clipId}>
                <rect x={x} y={actualY} width={barWidth} height={Math.max(actualH, 0)} rx={5} />
              </clipPath>

              <g clipPath={`url(#${clipId})`}>
                {/* White behind the stack, so the gaps between bands show white even
                    where the stack overlaps the grey projection bar. */}
                <rect x={x} y={actualY} width={barWidth} height={Math.max(actualH, 0)} fill="#FFFFFF" />
                {segments.map((seg, idx) => {
                  const segH = actualH * (seg.exits / m.exits);
                  segBottom -= segH;
                  // The gap is taken from the BOTTOM of every band but the lowest, so
                  // the stack still stands on the baseline and still totals actualH.
                  const gap = idx === 0 ? 0 : Math.min(SEG_GAP, segH * 0.4);
                  return (
                    <rect
                      key={seg.key}
                      data-testid={`attrition-seg-${m.month}-${seg.key}`}
                      x={x}
                      y={segBottom}
                      width={barWidth}
                      height={Math.max(segH - gap, 0)}
                      fill={seg.fill}
                      className="cursor-pointer"
                      onClick={() => onPick(m.month, seg.key)}
                    >
                      <title>
                        {seg.points == null
                          ? `Unclassified: ${seg.exits} exits (missing or invalid joining date)`
                          : `${seg.label}: ${seg.exits} exits, ${((seg.exits / m.exits) * 100).toFixed(1)}% of ${tenureFiltered ? 'the exits shown' : 'exits'}, ${seg.points.toFixed(seg.points < 0.1 ? 2 : 1)} attrition points${hintByKey.get(seg.key) ? `. ${hintByKey.get(seg.key)}` : ''}`}
                      </title>
                    </rect>
                  );
                })}
              </g>

              <text x={x + barWidth / 2} y={actualLabelY} textAnchor="middle" fill="#111827" fontSize="9.5" fontWeight="700">
                {fmtRate(m)}%
              </text>
              {showProjected && (
                <text x={x + barWidth / 2} y={projLabelY} textAnchor="middle" fill="#6B7280" fontSize="8.5" fontWeight="600">
                  {m.projected}% projected
                </text>
              )}
              <text x={x + barWidth / 2} y={BASE_Y + 15} textAnchor="middle" fill="#6B7280" fontSize="10">
                {m.label}
              </text>
            </g>
          );
        })}
      </svg>

      {/* Legend, grouped by stage. The colour FAMILY says which stage of employment an
          exit came from; the shade, light to dark, says where inside it. */}
      <div className="mt-2 flex flex-wrap justify-center gap-x-6 gap-y-2 text-[11px] text-gray-600">
        {groups.map((g) => (
          <div key={g.label ?? g.items[0].key} className="flex flex-wrap items-center gap-x-3 gap-y-1">
            {g.label && <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">{g.label}</span>}
            {g.items.map((b) => (
              <span key={b.key} className="inline-flex items-center gap-1.5" title={b.hint}>
                <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: b.hex }} />
                {b.label}
              </span>
            ))}
          </div>
        ))}
        {anyProjected && (
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-sm border border-dashed border-gray-400 bg-gray-100" />
            Projected total
          </span>
        )}
      </div>
    </>
  );
}

/**
 * The Managed vs Unmanaged view, drawn as 1Office Project Management's Attrition
 * Classification card draws it: per month, the exits HR processed that month, Managed and
 * Unmanaged stacked as COUNTS with the classified total above. Not recorded is not drawn
 * (it dwarfs the two in the months before 1Office began asking) but its total is in the
 * legend. Not a rate: these exits are counted by the month HR processed them, so they do
 * not divide by the month's headcount the way the tenure view does.
 */
function ExitTypeChart({ monthly, exitTypes, onPick }: {
  monthly: MonthlyRow[];
  exitTypes: Bucket[];
  onPick: (month: string, exitType: string) => void;
}) {
  const plotted = exitTypes.filter((t) => t.key !== NOT_RECORDED);
  const countOf = (m: MonthlyRow, key: string): number => m.exitTypes?.find((t) => t.key === key)?.exits ?? 0;
  const totals = monthly.map((m) => plotted.reduce((n, t) => n + countOf(m, t.key), 0));
  const maxValue = Math.max(...totals, 1);
  const n = monthly.length;
  const barWidth = Math.min((CHART_W - BAR_GAP * (n - 1)) / n, 62);
  const offsetX = PAD.left + (CHART_W - (n * barWidth + (n - 1) * BAR_GAP)) / 2;
  const notRecordedTotal = monthly.reduce((sum, m) => sum + countOf(m, NOT_RECORDED), 0);
  const notRecorded = exitTypes.find((t) => t.key === NOT_RECORDED);

  return (
    <>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img" aria-label="Exits processed each month, Managed and Unmanaged">
        <line x1={PAD.left} x2={W - PAD.right} y1={BASE_Y} y2={BASE_Y} stroke="#E5E7EB" strokeWidth="0.75" />
        {monthly.map((m, i) => {
          const x = offsetX + i * (barWidth + BAR_GAP);
          const total = totals[i];
          const barH = (total / maxValue) * CHART_H;
          const clipId = `attrition-types-${m.month}`;
          let segBottom = BASE_Y;
          return (
            <g key={m.month}>
              <title>{`${m.label}: ${total} classified of ${total + countOf(m, NOT_RECORDED)} exits processed`}</title>
              <clipPath id={clipId}>
                <rect x={x} y={BASE_Y - barH} width={barWidth} height={Math.max(barH, 0)} rx={5} />
              </clipPath>
              <g clipPath={`url(#${clipId})`}>
                <rect x={x} y={BASE_Y - barH} width={barWidth} height={Math.max(barH, 0)} fill="#FFFFFF" />
                {plotted.filter((t) => countOf(m, t.key) > 0).map((t, idx) => {
                  const count = countOf(m, t.key);
                  const segH = barH * (count / total);
                  segBottom -= segH;
                  const gap = idx === 0 ? 0 : Math.min(SEG_GAP, segH * 0.4);
                  return (
                    <rect
                      key={t.key}
                      data-testid={`attrition-seg-${m.month}-${t.key}`}
                      x={x}
                      y={segBottom}
                      width={barWidth}
                      height={Math.max(segH - gap, 0)}
                      fill={t.hex}
                      className="cursor-pointer"
                      onClick={() => onPick(m.month, t.key)}
                    >
                      <title>{`${t.label}: ${count} exit${count === 1 ? '' : 's'} processed in ${m.label}${t.hint ? `. ${t.hint}` : ''}`}</title>
                    </rect>
                  );
                })}
              </g>
              {plotted.filter((t) => countOf(m, t.key) > 0).length > 0 && (
                <text x={x + barWidth / 2} y={BASE_Y - barH - 4} textAnchor="middle" fill="#111827" fontSize="9.5" fontWeight="700">
                  {total}
                </text>
              )}
              <text x={x + barWidth / 2} y={BASE_Y + 15} textAnchor="middle" fill="#6B7280" fontSize="10">
                {m.label}
              </text>
            </g>
          );
        })}
      </svg>
      <div className="mt-2 flex flex-wrap justify-center gap-x-6 gap-y-2 text-[11px] text-gray-600">
        {plotted.map((t) => (
          <span key={t.key} className="inline-flex items-center gap-1.5" title={t.hint}>
            <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: t.hex }} />
            {t.label} ({monthly.reduce((sum, m) => sum + countOf(m, t.key), 0)})
          </span>
        ))}
        {notRecorded && (
          <span className="inline-flex items-center gap-1.5 text-gray-400" title={notRecorded.hint}>
            <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: notRecorded.hex }} />
            {notRecorded.label} ({notRecordedTotal}) · not plotted
          </span>
        )}
      </div>
    </>
  );
}

// ── Drill-down modal ─────────────────────────────────────────────────────────

function ExitedEmployeesModal({ rows: allRows, basis = 'left', buckets, exitTypes, windowMonths, initialMonth, initialBucket, initialExitType, teamScoped, regionOf, tenureTag, lookupFailed, scoresShown, scoresFailed, onClose }: {
  rows: ExitedEmployee[];
  /**
   * 'left': the people who left, by leaving month (the tenure chart, the Exits column).
   * 'processed': the exits HR processed in 1Office, by the month it processed them — the
   * people behind the Managed / Unmanaged numbers, as Project Management counts them.
   */
  basis?: 'left' | 'processed';
  buckets: Bucket[];
  /** Set when 1Office sends the Managed / Unmanaged choice: shows the column and its filter. */
  exitTypes?: Bucket[];
  /** The months of the chart ('YYYY-MM'), so a month with no leavers is still an option. */
  windowMonths: string[];
  initialMonth: string;
  initialBucket?: string;
  /** The exit type clicked on the chart. */
  initialExitType?: string;
  /** Set when the dashboard's own Tenure filter is on: `rows` already holds only those
   *  bands, so an export of "all tenure buckets" here is not all tenures. */
  tenureTag?: string;
  /** The store lookup failed, so blank location cells are a failure, not a finding. */
  lookupFailed?: boolean;
  /** The project has a scorecard: show Last score and 6-month avg. Off, the list is as before. */
  scoresShown?: boolean;
  /** The score read failed, so a dash there is a failure, not "never scored". */
  scoresFailed?: boolean;
  /** State → region, from the 1Office master. There is no region column anywhere;
   *  every Region on the dashboard is rolled up from the state this way. */
  regionOf: (state?: string | null) => string | undefined;
  /**
   * The viewer is hierarchy-scoped, so these rows are their own reporting line's
   * leavers only. This modal and its export are the most sensitive surface on the
   * tab — they carry names, joining and exit dates — so they must say whose.
   */
  teamScoped?: boolean;
  onClose: () => void;
}) {
  const [q, setQ] = useState('');
  const [month, setMonth] = useState(initialMonth);
  const [bucket, setBucket] = useState(initialBucket ?? '');
  const [exitType, setExitType] = useState(initialExitType ?? '');
  const exitTypeLabel = (r: ExitedEmployee): string =>
    exitTypes?.find((t) => t.key === (r.exitType ?? NOT_RECORDED))?.label ?? 'Not recorded';
  const processedBasis = basis === 'processed' && !!exitTypes;
  /** The month a row belongs to in this list. */
  const monthOf = (r: ExitedEmployee): string | null => (processedBasis ? r.processedMonth ?? null : r.month);
  // A leaver of the window can have been processed before it (and the reverse): each
  // list keeps only the people its own months count, so a total opens exactly its people.
  const rows = useMemo(
    () => allRows.filter((r) => (processedBasis ? !!r.processedMonth && windowMonths.includes(r.processedMonth) : !!r.month)),
    [allRows, processedBasis, windowMonths],
  );

  // The window's months, not just the months that have rows. Opening the list from a
  // month with no leavers used to leave the selector showing "All months" over an
  // empty table, because the month it was filtering on was not among its options.
  const months = useMemo(
    () => [...new Set([...windowMonths, ...rows.map((r) => (processedBasis ? r.processedMonth : r.month)).filter((m): m is string => !!m)])].sort(),
    [windowMonths, rows, processedBasis],
  );
  /** Region for a row. Three different blanks: no store on record, a store whose state
   *  the master has no region for, and a real region. */
  const regionCell = (r: ExitedEmployee): string => (r.state ? regionOf(r.state) || 'Not mapped' : '');

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (month && monthOf(r) !== month) return false;
      if (bucket && r.tenureBucketKey !== bucket) return false;
      if (processedBasis && exitType && (r.exitType ?? NOT_RECORDED) !== exitType) return false;
      if (!needle) return true;
      return [r.employeeName, r.employeeCode, r.project, r.budget, regionOf(r.state), r.state, r.city]
        .filter(Boolean).join(' ').toLowerCase().includes(needle);
    });
    // regionOf changes identity when the state master loads; regionCell only wraps it.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- monthOf only reads processedBasis
  }, [rows, q, month, bucket, exitType, regionOf, processedBasis]);

  const sort = useTableSort(filtered, {
    month: (r) => monthOf(r),
    employee: (r) => r.employeeName || r.employeeCode,
    project: (r) => r.project || '',
    budget: (r) => r.budget || '',
    region: (r) => regionCell(r),
    state: (r) => r.state || '',
    city: (r) => r.city || '',
    joining: (r) => r.joiningDate || '',
    exit: (r) => r.exitDate,
    processed: (r) => r.processedOn ?? null,
    // Pass the raw null, NOT a `?? -1` sentinel: useTableSort treats null as
    // MISSING and sinks it to the bottom in both directions. A -1 sentinel is a
    // real number, so ascending would float every unknown-tenure row to the top —
    // burying the shortest genuine tenures, which are the whole point of sorting
    // this column.
    tenure: (r) => r.tenureDays,
    bucket: (r) => r.tenureBucketLabel,
    exitType: (r) => exitTypeLabel(r),
    // null for a leaver with no scored month, so they sink in both directions.
    lastScore: (r) => r.score?.total ?? null,
    avg6m: (r) => r.score?.avg6m ?? null,
  });

  // Exports exactly what is on screen — the search / month / bucket filters and the
  // active sort — so a downloaded file always matches the rows the user was looking at.
  // Name and code go in SEPARATE columns (the table renders them as "Name (code)", which
  // is unusable as a spreadsheet key), and tenure ships both the readable string and the
  // raw day count so the file can be sorted or bucketed without re-parsing "2.6 years".
  const handleExport = () => {
    const headers = [processedBasis ? 'Processed month' : 'Month', 'Employee name', 'Employee code', 'Project', 'Budget',
      'Region', 'State', 'City', 'Last store',
      'Joining date', 'Exit date', ...(processedBasis ? ['Processed on'] : []), 'Tenure', 'Tenure (days)', 'Tenure bucket',
      // At the end, and only when the columns are shown: without a scorecard the file
      // keeps exactly the header it always had.
      ...(scoresShown ? ['Last score', 'Last score month', '6-month avg', 'Months averaged'] : []),
      ...(exitTypes ? ['Exit type'] : [])];
    const csvRows = sort.sorted.map((r) => [
      monthTitle(monthOf(r) ?? ''),
      r.employeeName || '',
      r.employeeCode,
      r.project || '',
      r.budget || '',
      regionCell(r),
      r.state || '',
      r.city || '',
      r.store || '',
      fmtDate(r.joiningDate),
      fmtDate(r.exitDate),
      ...(processedBasis ? [fmtDate(r.processedOn ?? null)] : []),
      fmtTenure(r.tenureDays),
      r.tenureDays == null ? '' : String(r.tenureDays),
      r.tenureBucketLabel,
      ...(scoresShown
        ? [
          r.score ? fmtScore(r.score.total) : '',
          r.score ? monthTitle(r.score.month) : '',
          r.score ? fmtScore(r.score.avg6m) : '',
          r.score ? String(r.score.monthsAveraged) : '',
        ]
        : []),
      ...(exitTypes ? [exitTypeLabel(r)] : []),
    ]);
    // "all-tenures" would be untrue when the dashboard's Tenure filter has already
    // narrowed these rows; the bands it kept are named instead.
    const scope = [month || 'all-months', bucket || tenureTag || 'all-tenures', ...(processedBasis && exitType ? [exitType] : [])].join('_');
    // Named leavers leaving the browser: the filename has to carry the scope, or a
    // forwarded file reads as the project's full exit list.
    downloadCsv(`${processedBasis ? 'Processed_exits' : 'Exited_employees'}${teamScoped ? '_MyTeam' : ''}_${scope}.csv`, buildCsv(headers, csvRows));
  };

  return (
    <Modal
      title={`${processedBasis ? 'Exits processed in 1Office' : 'Exited employees by tenure'}${teamScoped ? ' — your team' : ''}`}
      onClose={onClose}
      maxWidth="max-w-7xl"
    >
      <p className="mb-3 text-xs text-gray-500">
        {processedBasis
          ? 'Exits HR processed in 1Office, by the month it processed them, as 1Office Project Management counts Managed and Unmanaged. '
          : 'Employee rows reconcile the coloured sections in each monthly bar. '}
        Region, state and city are those of the employee's last store on record. A dash in State or City means no store is on record for them.
        {teamScoped ? ' These are the leavers from your own reporting line only.' : ''}
      </p>
      {lookupFailed && (
        <div className="mb-3 flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
          <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
          <span>Store locations could not be loaded just now, so Region, State and City are blank for most people. Reload to try again.</span>
        </div>
      )}
      {scoresShown && scoresFailed && (
        <div className="mb-3 flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
          <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
          <span>Employee Scores could not be loaded just now, so Last score and 6-month avg show a dash. Reload to try again.</span>
        </div>
      )}

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="min-w-[220px] flex-1"><SearchBox value={q} onChange={setQ} placeholder="Search name, code, project, budget, or location" /></div>
        <select value={month} onChange={(e) => setMonth(e.target.value)} className="rounded-lg border border-gray-200 px-3 py-2 text-sm" aria-label="Month">
          <option value="">All months</option>
          {months.map((m) => <option key={m} value={m}>{monthTitle(m)}</option>)}
        </select>
        <select value={bucket} onChange={(e) => setBucket(e.target.value)} className="rounded-lg border border-gray-200 px-3 py-2 text-sm" aria-label="Tenure bucket">
          <option value="">All tenure buckets</option>
          {buckets.map((b) => <option key={b.key} value={b.key}>{b.label}</option>)}
        </select>
        {processedBasis && exitTypes && (
          <select value={exitType} onChange={(e) => setExitType(e.target.value)} className="rounded-lg border border-gray-200 px-3 py-2 text-sm" aria-label="Exit type">
            <option value="">All exit types</option>
            {exitTypes.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
        )}
        {/* Disabled rather than hidden when the filters match nothing: a button that
            vanishes reads as a missing feature, and an empty CSV reads as a broken one. */}
        <button
          type="button"
          onClick={handleExport}
          disabled={sort.sorted.length === 0}
          className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Download className="h-4 w-4" />
          Download
        </button>
      </div>

      {/* Judged on the rows on screen, not the whole window: a list opened on one month
          would otherwise explain an Unclassified exit that is not in it. */}
      {filtered.some((r) => r.tenureBucketKey === 'unclassified') && (
        <div className="mb-3 flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
          <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
          <span>Unclassified exits have a missing or invalid joining date. They still count toward the monthly total, but cannot be placed in a tenure band.</span>
        </div>
      )}

      <div className="overflow-x-auto">
        {/* Wide enough that the name column does not wrap; Processed on adds a column. */}
        <table className={`w-full ${scoresShown
          ? (processedBasis ? 'min-w-[1600px]' : exitTypes ? 'min-w-[1460px]' : 'min-w-[1340px]')
          : (processedBasis ? 'min-w-[1440px]' : exitTypes ? 'min-w-[1300px]' : 'min-w-[1180px]')} text-sm`}>
          <thead>
            <tr className="border-b border-gray-100 text-left text-xs uppercase tracking-wider text-gray-400">
              <SortTh
                label={processedBasis ? 'Processed month' : 'Month'}
                sortKey="month"
                sort={sort}
                className="whitespace-nowrap pb-3 font-semibold"
              />
              <SortTh label="Employee" sortKey="employee" sort={sort} className="pb-3 pl-4 font-semibold" />
              {/* Next to the name: this table scrolls sideways, and the column it was asked for
                  must not start out of sight. */}
              {exitTypes && (
                <SortTh label="Exit type" sortKey="exitType" sort={sort} className="whitespace-nowrap pb-3 pl-4 font-semibold" title="How the approver classified this exit in 1Office." />
              )}
              <SortTh label="Project" sortKey="project" sort={sort} className="pb-3 pl-4 font-semibold" />
              {/* Budget is searchable (and the placeholder advertises it), so it
                  needs a column — otherwise a search matches rows for reasons the
                  user cannot see. */}
              <SortTh label="Budget" sortKey="budget" sort={sort} className="pb-3 pl-4 font-semibold" />
              <SortTh label="Region" sortKey="region" sort={sort} className="pb-3 pl-4 font-semibold" />
              <SortTh label="State" sortKey="state" sort={sort} className="pb-3 pl-4 font-semibold" />
              <SortTh label="City" sortKey="city" sort={sort} className="pb-3 pl-4 font-semibold" />
              <SortTh label="Joining date" sortKey="joining" sort={sort} className="whitespace-nowrap pb-3 pl-4 font-semibold" />
              <SortTh label="Exit date" sortKey="exit" sort={sort} className="whitespace-nowrap pb-3 pl-4 font-semibold" />
              {processedBasis && (
                <SortTh
                  label="Processed on"
                  sortKey="processed"
                  sort={sort}
                  className="whitespace-nowrap pb-3 pl-4 font-semibold"
                  title="The day ChannelPlex first saw the exit record in 1Office. For exits processed before ChannelPlex began noting this, the resignation date on the record."
                />
              )}
              <SortTh label="Tenure" sortKey="tenure" sort={sort} align="right" className="pb-3 pl-4 font-semibold" />
              <SortTh label="Tenure bucket" sortKey="bucket" sort={sort} className="pb-3 pl-4 font-semibold" />
              {scoresShown && (
                <>
                  <SortTh label="Last score" sortKey="lastScore" sort={sort} align="right" className="whitespace-nowrap pb-3 pl-4 font-semibold" title="The newest Employee Score stored for them on or before their exit month. Its month is shown when it is earlier." />
                  <SortTh label="6-month avg" sortKey="avg6m" sort={sort} align="right" className="whitespace-nowrap pb-3 pl-4 font-semibold" title="The average of their stored Employee Scores over the six months ending at the Last score's month. The number of months is shown when fewer than six were scored." />
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {sort.sorted.map((r) => (
              <tr key={`${r.employeeCode}:${r.exitDate ?? r.processedOn}`} className="border-b border-gray-50 hover:bg-gray-50/50">
                <td className="whitespace-nowrap py-3 text-gray-600">{monthTitle(monthOf(r) ?? '')}</td>
                <td className="py-3 pl-4 font-medium text-gray-900">
                  {r.employeeName || '—'}<span className="ml-1 text-xs font-normal text-gray-400">({r.employeeCode})</span>
                </td>
                {exitTypes && (
                  <td
                    className={`whitespace-nowrap py-3 pl-4 ${(r.exitType ?? NOT_RECORDED) === NOT_RECORDED ? 'text-gray-400' : 'text-gray-700'}`}
                    title={exitTypes.find((t) => t.key === (r.exitType ?? NOT_RECORDED))?.hint}
                  >
                    {exitTypeLabel(r)}
                  </td>
                )}
                <td className="py-3 pl-4 text-gray-700">{r.project || '—'}</td>
                <td className="py-3 pl-4 text-gray-700">{r.budget || '—'}</td>
                <td className={`whitespace-nowrap py-3 pl-4 ${r.state && !regionOf(r.state) ? 'text-gray-400' : 'text-gray-700'}`}>{regionCell(r) || '—'}</td>
                <td className="whitespace-nowrap py-3 pl-4 text-gray-700">{r.state || '—'}</td>
                <td className="whitespace-nowrap py-3 pl-4 text-gray-700" title={r.store || undefined}>{r.city || '—'}</td>
                <td className="whitespace-nowrap py-3 pl-4 text-gray-700">{fmtDate(r.joiningDate)}</td>
                <td className="whitespace-nowrap py-3 pl-4 text-gray-700" title={r.exitDate ? undefined : 'Not left yet'}>{fmtDate(r.exitDate)}</td>
                {processedBasis && (
                  <td className="whitespace-nowrap py-3 pl-4 text-gray-700">{fmtDate(r.processedOn ?? null)}</td>
                )}
                <td className="whitespace-nowrap py-3 pl-4 text-right text-gray-700">{fmtTenure(r.tenureDays)}</td>
                <td className="whitespace-nowrap py-3 pl-4 text-gray-700">{r.tenureBucketLabel}</td>
                {scoresShown && (
                  <>
                    <td className="whitespace-nowrap py-3 pl-4 text-right text-gray-700" title={lastScoreTitle(r, !!scoresFailed)}>
                      {r.score ? (
                        <>
                          <div className="font-semibold tabular-nums text-gray-900">{fmtScore(r.score.total)}</div>
                          {r.month && r.score.month !== r.month && (
                            <div className="mt-0.5 text-right text-[11px] text-gray-400">{monthTitle(r.score.month)}</div>
                          )}
                        </>
                      ) : '—'}
                    </td>
                    <td className="whitespace-nowrap py-3 pl-4 text-right text-gray-700" title={r.score ? avgTitle(r.score) : undefined}>
                      {r.score ? (
                        <>
                          <div className="tabular-nums">{fmtScore(r.score.avg6m)}</div>
                          {r.score.monthsAveraged < 6 && (
                            <div className="mt-0.5 text-[11px] text-gray-400">
                              ({r.score.monthsAveraged} month{r.score.monthsAveraged === 1 ? '' : 's'})
                            </div>
                          )}
                        </>
                      ) : '—'}
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        {sort.sorted.length === 0 && (
          <div className="py-8 text-center text-sm text-gray-500">No employees match the selected filters.</div>
        )}
      </div>
      {/* The second figure is every leaver in the window, whichever month is picked. Said
          so: "Showing 8 of 52" under one month reads as 52 people having left in it. */}
      <div className="mt-2 text-right text-xs text-gray-400">
        Showing {sort.sorted.length} of {rows.length} {processedBasis ? 'exits processed' : 'exited employees'} in the {months.length === 1 ? 'month' : `${months.length} months`} shown
      </div>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

export default function AttritionTab({ projectId, month, roleId, filters, tenures, onFacetsLoaded }: {
  projectId: number | null;
  month: string;
  // Look-back-safe filters only: Role / Area Manager / Trade Head / Budget, and
  // Region / State / City and Store Trade (people are placed by their last store on record). The
  // backend ignores the other store / standing keys `filters` carries from shared state.
  roleId?: string;
  filters?: DashRichFilterParams;
  /** CSV of tenure-band keys. Narrows which EXITS are counted; the headcount stays. */
  tenures?: string;
  /**
   * Called with what each response says the filter modal should offer: the tenure
   * bands the API counts, and where this viewer's leavers were last posted. The modal's
   * own lists come from a hand-kept copy and from the stores currently staffed, and
   * miss a renamed band or a city whose only store has closed.
   */
  onFacetsLoaded?: (facets: AttritionFacets) => void;
}) {
  const [data, setData] = useState<AttritionData | null>(null);
  const [meta, setMeta] = useState<AttritionMeta | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // basis 'processed' lists the exits HR processed (the Managed / Unmanaged numbers); the
  // default lists the people who left.
  const [drill, setDrill] = useState<{ month: string; bucket?: string; exitType?: string; basis?: 'left' | 'processed' } | null>(null);
  const [chartViewChoice, setChartViewChoice] = useState<ChartView>('tenure');
  // The list the address names (?list=exits): where the home screen's "N exits this
  // month" lands.
  const { list, closeList } = useDashboardList();
  const [breakdown, setBreakdown] = useState<AttritionBreakdown | null>(null);
  const [breakdownLoading, setBreakdownLoading] = useState(false);
  const [breakdownError, setBreakdownError] = useState<string | null>(null);
  const [breakdownDim, setBreakdownDim] = useState<AttrDim>('manager');
  const breakdownReq = useRef(0);
  const { regionOf } = useStoreRegions();
  // Whether the server offers this viewer the Project Management link for this project.
  // For anyone the server does not offer it to (no grant, or the feature stopped with
  // PM_LAUNCH_ENABLED=false), the tab is exactly as it was.
  const pmLaunch = usePmLaunchStatus(projectId, 'attrition-classification');
  const reqRef = useRef(0);
  // Held in a ref so a parent passing a fresh function each render cannot refetch.
  const onFacetsLoadedRef = useRef(onFacetsLoaded);
  useEffect(() => { onFacetsLoadedRef.current = onFacetsLoaded; }, [onFacetsLoaded]);

  useEffect(() => {
    if (!projectId) return;
    const reqId = ++reqRef.current;
    setLoading(true);
    setError(null);
    attritionDashboardAPI
      .getDashboard(projectId, month, roleId, filters, tenures)
      .then((res) => {
        if (reqId !== reqRef.current) return;
        const next = (res.data?.data ?? null) as AttritionData | null;
        setData(next);
        setMeta((res.data?.meta ?? null) as AttritionMeta | null);
        if (next?.buckets?.length) {
          onFacetsLoadedRef.current?.({
            tenures: next.buckets.map((b) => ({ key: b.key, label: b.label })),
            locations: Array.isArray(next.locations) ? next.locations : [],
            trades: Array.isArray(next.trades) ? next.trades.filter((t) => typeof t === 'string') : [],
          });
        }
      })
      .catch((err: unknown) => {
        if (reqId !== reqRef.current) return;
        const e = err as { response?: { data?: { message?: string } } };
        setError(e.response?.data?.message || 'Failed to load the attrition dashboard');
        setData(null);
      })
      .finally(() => { if (reqId === reqRef.current) setLoading(false); });
    // `filters` is a memoized object in the parent (dashFilterParams), so it only
    // changes identity when a value actually changes — safe as an effect dep.
  }, [projectId, month, roleId, filters, tenures]);

  // ── The month's exits, grouped by manager / store attributes ──
  // Separate call from the dashboard above: same roster and scope server-side, but
  // it needs a per-leaver store + manager join the rate maths has no use for.
  useEffect(() => {
    if (!projectId) return;
    const reqId = ++breakdownReq.current;
    setBreakdownLoading(true);
    setBreakdownError(null);
    attritionDashboardAPI
      .getBreakdown(projectId, month, roleId, filters, tenures)
      .then((res) => {
        if (reqId !== breakdownReq.current) return;
        setBreakdown((res.data?.data ?? null) as AttritionBreakdown | null);
      })
      .catch((err: unknown) => {
        if (reqId !== breakdownReq.current) return;
        const e = err as { response?: { data?: { message?: string } } };
        setBreakdownError(e.response?.data?.message || 'Failed to load the attrition breakdown');
        setBreakdown(null);
      })
      .finally(() => { if (reqId === breakdownReq.current) setBreakdownLoading(false); });
  }, [projectId, month, roleId, filters, tenures]);

  // Rows for the selected dimension. Manager / Trade / Category / City come straight
  // from the server; REGION is rolled up here from the states, because 1Office's
  // state master is the only state→region authority (as everywhere else).
  const breakdownRows = useMemo(() => {
    if (!breakdown) return [] as Array<{ label: string; exits: number; managed: number; unmanaged: number; notRecorded: number }>;
    const direct =
      breakdownDim === 'manager' ? breakdown.byManager
      : breakdownDim === 'trade' ? breakdown.byTrade
      : breakdownDim === 'category' ? breakdown.byCategory
      : breakdownDim === 'city' ? breakdown.byCity
      : null;
    if (direct) {
      const blank = breakdownDim === 'manager' ? NO_MANAGER : NO_STORE_ATTR;
      return direct.map((b) => ({ label: b.key || blank, exits: b.exits, managed: b.managed ?? 0, unmanaged: b.unmanaged ?? 0, notRecorded: b.notRecorded ?? 0 }));
    }
    const agg = new Map<string, { label: string; exits: number; managed: number; unmanaged: number; notRecorded: number }>();
    for (const b of breakdown.byState) {
      const label = b.key ? (regionOf(b.key) || 'Region not mapped') : NO_STORE_ATTR;
      const row = agg.get(label) ?? { label, exits: 0, managed: 0, unmanaged: 0, notRecorded: 0 };
      row.exits += b.exits;
      row.managed += b.managed ?? 0;
      row.unmanaged += b.unmanaged ?? 0;
      row.notRecorded += b.notRecorded ?? 0;
      agg.set(label, row);
    }
    return [...agg.values()]
      .sort((a, b) => b.exits - a.exits || a.label.localeCompare(b.label));
  }, [breakdown, breakdownDim, regionOf]);

  // A leaver list named in the address that this answer will not open (nobody left in the
  // month, the exit records look missing, a team panel or an error shows instead) comes
  // out of the address once the answer is in. Left there, it would open the pop-up by
  // itself the moment a later Month or Role change brings someone in, with no click. The
  // tab is drawn only once the default Role filter has landed, so the answer that decides
  // this is the one the home screen's number was counted from; an answer still on its way
  // decides nothing.
  const exitsListWillNotOpen = list === EXITS_SLUG && !loading && (!!error || data !== null) && !(
    data !== null
    && data.monthly.length > 0
    && !(meta?.scope_mode === 'team' && (meta.scoped_reportee_count === 0 || (meta.filtered && meta.roster_size === 0)))
    && !meta?.exits_feed_suspect
    && data.employees.some((e) => e.month === data.anchorMonth)
  );
  useEffect(() => {
    if (exitsListWillNotOpen) closeList();
  }, [exitsListWillNotOpen, closeList]);

  // The Managed / Unmanaged split, when 1Office sends the choice. Without it the tab is
  // exactly as it was: no switch, no columns, and the chart stays on tenure.
  const exitTypes = meta?.exit_types_shown && data?.exitTypes?.length ? data.exitTypes : undefined;
  const chartView: ChartView = exitTypes ? chartViewChoice : 'tenure';
  const exitCount = (m: MonthlyRow, key: string): number => m.exitTypes?.find((t) => t.key === key)?.exits ?? 0;

  // Sort hook must be unconditional — declared before the early returns below.
  const monthlyRows = data?.monthly ?? [];
  const monthlySort = useTableSort(monthlyRows, {
    // 'YYYY-MM' sorts chronologically as a string.
    month: (m: MonthlyRow) => m.month,
    headcount: (m: MonthlyRow) => m.headcount,
    exits: (m: MonthlyRow) => m.exits,
    managed: (m: MonthlyRow) => exitCount(m, 'managed'),
    unmanaged: (m: MonthlyRow) => exitCount(m, 'unmanaged'),
    notRecorded: (m: MonthlyRow) => exitCount(m, NOT_RECORDED),
    actual: (m: MonthlyRow) => m.actual,
    // The cell shows '—' when there is no projection, so those months sort as missing.
    projected: (m: MonthlyRow) => (m.projected > 0 ? m.projected : null),
  });

  // The tenure bands the dashboard filter kept, for file names: "0-7-days+7-30-days".
  const tenureTag = (tenures ?? '').split(',').map((t) => t.trim()).filter(Boolean).join('+');

  const handleDownload = () => {
    if (!data) return;
    // Under a tenure filter the two figures are a SHARE of the rate and a subset of
    // the exits. The on-screen caption says so; the file has to say so itself, in its
    // headers and its name, because it is read without the screen. An unfiltered
    // export keeps its headers and name exactly as they were.
    const tenureOn = !!meta?.tenure_filtered;
    const headers = [
      'Month', 'Avg Daily Headcount',
      tenureOn ? 'Exits (selected tenure bands)' : 'Exits',
      tenureOn ? 'Share of attrition % (selected tenure bands)' : 'Attrition %',
      tenureOn ? 'Projected share %' : 'Projected %',
      ...data.buckets.filter((b) => b.key !== 'unclassified').map((b) => b.label), 'Unclassified',
      // At the end, and only when shown: without the split the file keeps the header it had.
      // Exits HR processed in the month, so the file says so: they need not add up to Exits.
      ...(exitTypes ?? []).map((t) => `${t.label} (processed in month${tenureOn ? ', selected tenure bands' : ''})`),
    ];
    const rows = data.monthly.map((m) => [
      monthTitle(m.month), String(m.headcount), String(m.exits), fmtRate(m),
      m.projected > 0 ? String(m.projected) : '',
      ...m.tenureBuckets.map((b) => String(b.exits)),
      String(m.unclassifiedExits),
      ...(exitTypes ?? []).map((t) => String(exitCount(m, t.key))),
    ]);
    // The scope has to survive the export. A file called Attrition_2026-09.csv
    // holding one reporting line's numbers reads as the project's attrition the
    // moment it leaves the browser, and nothing in the rows says otherwise.
    // Scope tag only. A _Filtered tag would also rename the ADMIN's export, which
    // must stay byte-identical to before this change; team scope is unreachable
    // for a project-wide viewer, so this suffix never touches them.
    const scopeTag = meta?.scope_mode === 'team' ? '_MyTeam' : '';
    downloadCsv(`Attrition${scopeTag}${tenureOn ? `_tenure-${tenureTag}` : ''}_${data.anchorMonth}.csv`, buildCsv(headers, rows));
  };

  // 1Office Project Management's Managed vs Unmanaged Attrition page, which ChannelPlex has
  // no figures for. Shown only when the server offers it to this viewer for this project.
  // A plain link to the web's own "Opening…" page in a new tab — that page asks for the
  // one-time sign-in link and moves on to it.
  const pmLaunchLink = pmLaunch.available && projectId != null ? (
    <a
      href={pmLaunchHref(projectId, 'attrition-classification')}
      target="_blank"
      rel="noopener noreferrer"
      title="Opens the Managed vs Unmanaged Attrition page of 1Office Project Management in a new tab, without a second login. It shows your own Project Management projects for the last six months."
      className="inline-flex items-center gap-1 rounded-full border border-primary-200 bg-white px-3 py-1 text-xs font-medium text-primary-600 hover:bg-primary-50"
    >
      {/* Beside the chart's own Managed vs Unmanaged switch, the same words would name two things. */}
      {exitTypes ? '1Office Project Management' : 'Managed vs Unmanaged'} <ExternalLink className="h-3.5 w-3.5" />
    </a>
  ) : null;
  // The link does not depend on ChannelPlex's own attrition figures, so it is offered in
  // the states below too — someone granted it who has no reportees here, or whose figures
  // failed to load, would otherwise have nowhere to click. Without the link the panel is
  // returned exactly as it always was.
  const withPmLaunchLink = (panel: ReactElement): ReactElement =>
    pmLaunchLink ? (
      <div className="space-y-3">
        <div className="flex justify-end">{pmLaunchLink}</div>
        {panel}
      </div>
    ) : panel;

  if (loading) return <div className="py-16 text-center text-sm text-gray-400"><InlineLoader label="Loading attrition…" className="flex justify-center" /></div>;
  if (error) return withPmLaunchLink(<div className="rounded-xl border border-rose-100 bg-rose-50 px-4 py-8 text-center text-sm text-rose-600">{error}</div>);
  if (!data || data.monthly.length === 0) return withPmLaunchLink(<div className="py-16 text-center text-sm text-gray-400">No attrition data available.</div>);
  // A viewer with nobody under them would otherwise get a chart of clean zeros,
  // which reads as "my team has no attrition" rather than "there is no team here".
  // This replaces the 403 those viewers used to get, so it has to say something.
  if (meta?.scope_mode === 'team' && meta.scoped_reportee_count === 0) {
    return withPmLaunchLink(
      <div className="rounded-2xl border border-gray-200 bg-white px-4 py-12 text-center shadow-sm">
        <p className="text-sm font-medium text-gray-700">No one reports to you on this project.</p>
        <p className="mt-1 text-xs text-gray-500">
          Attrition here covers your own reporting line. Ask an administrator if you should have project-wide access.
        </p>
      </div>,
    );
  }
  // Separately: they DO have a team, but the current filters match nobody in it.
  // Without this the chart draws clean zeros, which reads as "no attrition".
  if (meta?.scope_mode === 'team' && meta.filtered && meta.roster_size === 0) {
    return withPmLaunchLink(
      <div className="rounded-2xl border border-gray-200 bg-white px-4 py-12 text-center shadow-sm">
        <p className="text-sm font-medium text-gray-700">No one in your team matches these filters.</p>
        <p className="mt-1 text-xs text-gray-500">Clear a filter to see your reporting line's attrition.</p>
      </div>,
    );
  }

  // The leaver list in the pop-up. One named in the address is DERIVED from it on every
  // render, never copied into state: the dashboard asks again a moment after it opens
  // (when the default Role filter lands), and a list opened once on arrival would be shut
  // by that. It opens on the month on screen, the month the home screen counted, and only
  // when someone left in it: "0 exits this month" lands on the dashboard, not on an empty
  // pop-up. Nor while the exit records look missing: the home screen showed no number
  // then, and the warning above the chart must not be hidden under the list.
  const addressList = list === EXITS_SLUG && !meta?.exits_feed_suspect && data.employees.some((e) => e.month === data.anchorMonth)
    ? { month: data.anchorMonth }
    : null;
  const shown: typeof drill = drill ?? addressList;
  const closeDrill = () => {
    setDrill(null);
    if (list === EXITS_SLUG) closeList();
  };

  return (
    <div className="space-y-4">
      {/* A sizeable roster with no exit records at all is far more likely an
          upstream hiccup than a project nobody has ever left — and the upstream
          fails by returning an empty body, not an error. Say so, rather than let
          a clean 0% chart be read as fact. */}
      {meta?.exits_feed_suspect && (
        <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800">
          <Info className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>
            No exit records came back for this project, despite an active roster of {(meta.roster_size ?? 0).toLocaleString()}.
            The rates below may be understated — treat a 0% month as “not reported” rather than “nobody left”, and refresh
            before acting on it.
          </span>
        </div>
      )}

      {/* The three summary cards that used to sit here were removed as redundant:
          every number on them (latest month's rate, exits, headcount, projection)
          is already in the chart and the monthly table directly below.
          Their ONE non-duplicated fact — the count of leavers excluded for having
          no exit date — moved into the chart footnote, because it is a caveat on
          the figures rather than a headline. Do not drop it: those people are
          missing from every rate on this page. */}

      <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="mb-2 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Attrition Rate</h3>
            <p className="text-xs text-gray-500">Monthly trend by {chartView === 'exitType' ? 'exit type' : 'tenure'} — ending {monthTitle(data.anchorMonth)}</p>
            {/* A filtered view is a sub-group's OWN rate (its own exits over its own
                headcount), so it legitimately differs from the project figure and
                from Project Management. Say so, so a lower/higher number doesn't read
                as a bug — this was the reason filters were previously withheld. */}
            {/* Same reasoning as the filtered caption, for the viewer's ACCESS scope
                rather than their filter choices: a Trade Head sees their own
                reporting line, which is its own population and so its own rate.
                Shown first because it frames everything else on the tab. */}
            {/* ONE line, not two stacked ones: scope and filter both narrow the same
                population, and shown separately they contradicted each other over
                whose headcount the count described. The number quoted is
                meta.roster_size — the population the figures were ACTUALLY computed
                over, after both — so the caption can never overstate the group. */}
            {(meta?.scope_mode === 'team' || meta?.filtered) && (
              <p className="mt-0.5 text-xs font-medium text-amber-600">
                {meta.scope_mode === 'team' && meta.filtered
                  ? 'Your team, filtered — these cover the selected group inside your own reporting line'
                  : meta.scope_mode === 'team'
                    ? "Your team — this is your own reporting line's attrition rate"
                    : "Filtered view — this is the selected group's own attrition rate"}
                {typeof meta.roster_size === 'number'
                  ? ` (${meta.roster_size.toLocaleString()} ${meta.roster_size === 1 ? 'person' : 'people'})`
                  : ''}
                , not the whole project's.
              </p>
            )}
            {/* A location view has a gap it cannot close from here: a leaver with no
                store on record cannot be placed in any state or city, so they are in
                none of these figures. The server counts them; say how many, so a low
                rate for an older month is read with that in mind. */}
            {meta?.location_filtered && (
              <p className="mt-0.5 text-xs text-gray-500">
                By store location or trade: each person is placed by their last store on record, for every month shown.
                {meta.location_unmapped_exits
                  ? ` ${meta.location_unmapped_exits.toLocaleString()} leaver${meta.location_unmapped_exits === 1 ? '' : 's'} in these six months ${meta.location_unmapped_exits === 1 ? 'has' : 'have'} no store on record and ${meta.location_unmapped_exits === 1 ? 'is' : 'are'} not in this view.`
                  : ''}
              </p>
            )}
            {/* Tenure narrows the exits, not the headcount. Without this line a bar
                reading 0.9% under "0 to 7 days" looks like the attrition rate of new
                joiners; it is new joiners' share of the whole group's rate. */}
            {meta?.tenure_filtered && (
              <p className="mt-0.5 text-xs font-medium text-amber-600">
                Showing only exits with tenure: {(meta.tenure_labels ?? []).join(', ')}. Each bar is those exits as a share of the
                whole group's headcount — their part of the attrition rate, not a rate of their own.
              </p>
            )}
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            {exitTypes && (
              <div className="flex w-fit flex-wrap items-center gap-1 rounded-lg border border-gray-100 bg-gray-50 p-1">
                {CHART_VIEWS.map((v) => (
                  <button
                    key={v.key}
                    type="button"
                    onClick={() => setChartViewChoice(v.key)}
                    className={`rounded-md px-3 py-1 text-xs font-semibold transition-colors ${
                      chartView === v.key ? 'bg-white text-primary-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'
                    }`}
                  >
                    {v.label}
                  </button>
                ))}
              </div>
            )}
            <button type="button" onClick={handleDownload} className="inline-flex items-center gap-1 rounded-full border border-gray-200 px-3 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50">
              <Download className="h-3.5 w-3.5" /> CSV
            </button>
            <button type="button" onClick={() => setDrill({ month: '', basis: chartView === 'exitType' ? 'processed' : 'left' })} className="inline-flex items-center gap-1 rounded-full border border-primary-200 px-3 py-1 text-xs font-medium text-primary-600 hover:bg-primary-50">
              View All <ArrowRight className="h-3.5 w-3.5" />
            </button>
            {pmLaunchLink}
          </div>
        </div>
        <div className="mx-auto w-full max-w-5xl">
          {chartView === 'exitType' && exitTypes ? (
            <ExitTypeChart
              monthly={data.monthly}
              exitTypes={exitTypes}
              onPick={(m, key) => setDrill({ month: m, exitType: key, basis: 'processed' })}
            />
          ) : (
            <AttritionChart
              monthly={data.monthly}
              buckets={data.buckets}
              tenureFiltered={!!meta?.tenure_filtered}
              onPick={(m, key) => setDrill({ month: m, bucket: key })}
            />
          )}
        </div>
        <p className="mt-3 text-[11px] leading-5 text-gray-500">
          {chartView === 'exitType' ? (
            <>
              Exits HR processed in 1Office, by the month it processed them, split by the choice on the processed
              request — counted as 1Office Project Management counts Managed and Unmanaged. Counts, not rates, and not
              the leavers: someone switched off without HR processing the exit is in Exits but not here.
            </>
          ) : (
            <>
              Actual attrition is exits divided by average daily headcount. The stacked sections group exits by tenure at
              the exit date. The current month is still in progress, so its actual rate counts part-month exits against a
              full-month headcount — the grey dashed bar is the run-rate projection. Colours run in three families by stage
              of employment (first 3 months, 3 months to 2 years, over 2 years), light to dark as tenure grows.
            </>
          )}
          {!!meta?.dropped_no_exit_date && (
            <>
              {' '}
              <span className="text-amber-700">
                {meta.dropped_no_exit_date} leaver{meta.dropped_no_exit_date === 1 ? ' is' : 's are'} excluded from every
                figure above — 1Office holds no exit date for them, so they cannot be placed in a month.
              </span>
            </>
          )}
        </p>
      </section>

      {/* ─── Module: where the month's exits came from ─── */}
      <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Exits by manager & store</h3>
            <p className="text-xs text-gray-500">
              {monthTitle(breakdown?.month ?? data.anchorMonth)} — {(breakdown?.totalExits ?? 0).toLocaleString()} exit
              {(breakdown?.totalExits ?? 0) === 1 ? '' : 's'}, count and share. Exit COUNTS, not rates: a rate needs that
              group's own average headcount, which can't be reconstructed for people who have left.
              {exitTypes ? ' Managed, Unmanaged and Not recorded count the exits HR processed in 1Office this month, as Project Management does.' : ''}
            </p>
            {/* This card is scoped server-side by the same subtree filter as the
                chart above, so without this line it is the one surface that shows a
                team's exits under a project-sounding heading. */}
            {meta?.scope_mode === 'team' && (
              <p className="mt-0.5 text-xs font-medium text-amber-600">
                Your team — exits from your own reporting line only.
              </p>
            )}
            {/* The count and shares below are of the exits the Tenure filter kept. */}
            {meta?.tenure_filtered && (
              <p className="mt-0.5 text-xs font-medium text-amber-600">
                Only exits with tenure: {(meta.tenure_labels ?? []).join(', ')}.
              </p>
            )}
          </div>
          <div className="flex w-fit flex-wrap items-center gap-1 rounded-lg border border-gray-100 bg-gray-50 p-1">
            {ATTR_DIMS.map((d) => (
              <button
                key={d.key}
                type="button"
                onClick={() => setBreakdownDim(d.key)}
                className={`rounded-md px-3 py-1 text-xs font-semibold transition-colors ${
                  breakdownDim === d.key ? 'bg-white text-primary-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                {d.label}
              </button>
            ))}
          </div>
        </div>
        {breakdownLoading ? (
          <div className="py-8 text-center text-sm text-gray-500"><InlineLoader label="Loading…" className="flex justify-center" /></div>
        ) : breakdownError ? (
          <div className="py-8 text-center text-sm text-rose-600">{breakdownError}</div>
        ) : !breakdown || (breakdown.totalExits === 0 && !(exitTypes && breakdown.processedExits)) || breakdownRows.length === 0 ? (
          <div className="py-8 text-center text-sm text-gray-500">No exits in this month.</div>
        ) : (
          <div className={exitTypes ? 'overflow-x-auto' : undefined}>
            <div className={`mt-3 space-y-2 ${exitTypes ? 'min-w-[820px]' : ''}`}>
              {exitTypes && (
                <div className="flex items-center gap-3 border-b border-gray-100 pb-2 text-xs font-semibold uppercase tracking-wider text-gray-400">
                  <span className="w-44 shrink-0">{ATTR_DIMS.find((d) => d.key === breakdownDim)?.label}</span>
                  <span className="flex-1">Share</span>
                  <span className="w-24 shrink-0 text-right">Exits</span>
                  {exitTypes.map((t) => (
                    <span key={t.key} className="inline-flex w-32 shrink-0 items-center justify-end gap-1 whitespace-nowrap" title={t.hint}>
                      <span className="h-2 w-2 shrink-0 rounded-sm" style={{ backgroundColor: t.hex }} />
                      {t.label}
                    </span>
                  ))}
                </div>
              )}
              {breakdownRows.map((r) => {
                const share = breakdown.totalExits > 0 ? (r.exits / breakdown.totalExits) * 100 : 0;
                const splitOf = (key: string): number =>
                  key === 'managed' ? r.managed : key === 'unmanaged' ? r.unmanaged : r.notRecorded;
                // The parts are exits HR processed, so they are drawn against that total, not the leavers'.
                const processedTotal = breakdown.processedExits ?? breakdown.totalExits;
                return (
                  <div key={r.label} className="flex items-center gap-3">
                    <span className="w-44 shrink-0 truncate text-sm text-gray-700" title={r.label}>{r.label}</span>
                    {exitTypes ? (
                      <span className="flex h-2.5 flex-1 gap-px overflow-hidden rounded-full bg-gray-100">
                        {exitTypes.filter((t) => splitOf(t.key) > 0).map((t) => (
                          <span
                            key={t.key}
                            data-testid={`attrition-breakdown-part-${t.key}`}
                            className="block h-full"
                            style={{ width: `${processedTotal > 0 ? Math.max((splitOf(t.key) / processedTotal) * 100, 1) : 0}%`, backgroundColor: t.hex }}
                          />
                        ))}
                      </span>
                    ) : (
                      <span className="h-2.5 flex-1 overflow-hidden rounded-full bg-gray-100">
                        {/* Floor a non-zero share at 1% so a single exit stays visible. */}
                        <span className="block h-full rounded-full bg-primary-500" style={{ width: `${share > 0 ? Math.max(share, 1) : 0}%` }} />
                      </span>
                    )}
                    <span className="w-24 shrink-0 text-right text-sm">
                      <span className="font-semibold text-gray-900">{r.exits.toLocaleString()}</span>
                      <span className="ml-1 text-xs text-gray-500">({share.toFixed(1)}%)</span>
                    </span>
                    {exitTypes?.map((t) => (
                      <span key={t.key} className="w-32 shrink-0 text-right text-sm tabular-nums text-gray-700">{splitOf(t.key).toLocaleString()}</span>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </section>

      <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
        <h3 className="mb-2 text-lg font-semibold text-gray-900">Monthly summary</h3>
        {/* Exits and Actual are a subset and a share under a Tenure filter — the same
            caveat as the chart, repeated because this table is read on its own. */}
        {meta?.tenure_filtered && (
          <p className="-mt-1 mb-2 text-xs font-medium text-amber-600">
            Exits and Actual count only tenure: {(meta.tenure_labels ?? []).join(', ')}. Actual is their share of the
            attrition rate; headcount is the whole group's.
          </p>
        )}
        <div className="overflow-x-auto">
          <table className={`w-full ${exitTypes ? 'min-w-[900px]' : 'min-w-[640px]'} text-sm`}>
            <thead>
              <tr className="border-b border-gray-100 text-left text-xs uppercase tracking-wider text-gray-400">
                <SortTh label="Month" sortKey="month" sort={monthlySort} className="pb-3 font-semibold" />
                <SortTh label="Avg daily headcount" sortKey="headcount" sort={monthlySort} align="right" className="whitespace-nowrap pb-3 pl-4 text-right font-semibold" />
                <SortTh label="Exits" sortKey="exits" sort={monthlySort} align="right" className="pb-3 pl-4 text-right font-semibold" />
                {exitTypes && (
                  <>
                    <SortTh label="Managed" sortKey="managed" sort={monthlySort} align="right" className="pb-3 pl-4 text-right font-semibold" title={`${PROCESSED_HINT} ${exitTypes.find((t) => t.key === 'managed')?.hint ?? ''}`} />
                    <SortTh label="Unmanaged" sortKey="unmanaged" sort={monthlySort} align="right" className="pb-3 pl-4 text-right font-semibold" title={`${PROCESSED_HINT} ${exitTypes.find((t) => t.key === 'unmanaged')?.hint ?? ''}`} />
                    <SortTh label="Not recorded" sortKey="notRecorded" sort={monthlySort} align="right" className="whitespace-nowrap pb-3 pl-4 text-right font-semibold" title={`${PROCESSED_HINT} ${exitTypes.find((t) => t.key === NOT_RECORDED)?.hint ?? ''}`} />
                  </>
                )}
                <SortTh label="Actual" sortKey="actual" sort={monthlySort} align="right" className="whitespace-nowrap pb-3 pl-4 text-right font-semibold" />
                <SortTh label="Projected" sortKey="projected" sort={monthlySort} align="right" className="whitespace-nowrap pb-3 pl-4 text-right font-semibold" />
              </tr>
            </thead>
            <tbody>
              {monthlySort.sorted.map((m) => (
                <tr key={m.month} className="cursor-pointer border-b border-gray-50 hover:bg-gray-50/50" onClick={() => setDrill({ month: m.month })}>
                  <td className="py-3 font-medium text-gray-900">{monthTitle(m.month)}</td>
                  <td className="py-3 pl-4 text-right text-gray-700">{m.headcount.toLocaleString()}</td>
                  <td className="py-3 pl-4 text-right text-gray-700">{m.exits}</td>
                  {exitTypes && [ 'managed', 'unmanaged', NOT_RECORDED ].map((key) => (
                    // Opens the exits HR processed this month of that type, not the leavers.
                    <td
                      key={key}
                      data-testid={`attrition-summary-${m.month}-${key}`}
                      className="py-3 pl-4 text-right text-gray-700 hover:text-primary-600 hover:underline"
                      onClick={(e) => { e.stopPropagation(); setDrill({ month: m.month, exitType: key, basis: 'processed' }); }}
                    >
                      {exitCount(m, key)}
                    </td>
                  ))}
                  <td className="py-3 pl-4 text-right font-semibold text-primary-600">{fmtRate(m)}%</td>
                  <td className="py-3 pl-4 text-right text-gray-500">{m.projected > 0 ? `${m.projected}%` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {shown && createPortal(
        <ExitedEmployeesModal
          rows={data.employees}
          basis={shown.basis === 'processed' ? 'processed' : 'left'}
          buckets={data.buckets}
          exitTypes={exitTypes}
          windowMonths={data.monthly.map((m) => m.month)}
          initialMonth={shown.month}
          initialBucket={shown.bucket}
          initialExitType={shown.exitType}
          regionOf={regionOf}
          tenureTag={meta?.tenure_filtered ? tenureTag : undefined}
          lookupFailed={!!meta?.location_lookup_failed}
          scoresShown={!!meta?.scores_shown}
          scoresFailed={!!meta?.scores_lookup_failed}
          teamScoped={meta?.scope_mode === 'team'}
          onClose={closeDrill}
        />,
        document.body,
      )}
    </div>
  );
}
